import { useMemo } from "react";
import type { PublicStore, Site, TemplateDetail } from "../../../lib/api";
import type { SiteDocument } from "@zirro/schema";
import { useProducts, useStore } from "../../../lib/store-api";
import { getSampleStoreForTemplate } from "../../templates/sampleStore";

/**
 * The owner's store shaped like the public storefront, so the editor and
 * preview can render real products before publishing. Only live (active)
 * products are shown, as on the published site. If the site is based on a store
 * template or has store pages but has not added custom products yet, provides
 * the sample store so the template preview and editor remain fully interactive.
 */
export function usePreviewStore(
  site: Pick<Site, "id" | "name" | "slug"> | null,
  template?: TemplateDetail | null,
  doc?: SiteDocument | null,
): PublicStore | null {
  const store = useStore(site?.id);
  const products = useProducts(store.data ? site?.id : undefined);

  return useMemo(() => {
    if (site && store.data && products.data) {
      return {
        siteId: site.id,
        name: site.name,
        slug: site.slug,
        category: store.data.category,
        currency: store.data.currency,
        deliveryOptions: store.data.deliveryOptions,
        paymentMethods: [],
        productLayout: store.data.productLayout ?? "classic",
        products: products.data.filter((p) => p.status === "active"),
      };
    }

    const isStore =
      template?.kind === "store" ||
      template?.name?.toLowerCase().includes("store") ||
      doc?.pages?.some((p) => p.slug === "/shop");

    if (template && isStore) {
      const sample = getSampleStoreForTemplate(template);
      return {
        ...sample,
        siteId: site?.id ?? template.id,
        name: site?.name ?? sample.name,
        slug: site?.slug ?? sample.slug,
      };
    }

    if (isStore && site) {
      const sample = getSampleStoreForTemplate({
        id: site.id,
        name: site.name,
        description: "",
        kind: "store",
        categories: ["fashion"],
        coverImage: { url: "" },
        document: {} as any,
        createdAt: "",
        updatedAt: "",
        variations: [],
        sampleBusiness: { name: site.name, description: "", email: "", phone: "" },
      });
      return {
        ...sample,
        siteId: site.id,
        name: site.name,
        slug: site.slug,
      };
    }

    return null;
  }, [site, store.data, products.data, template, doc]);
}
